import React, {useEffect, useMemo, useState} from "react";
import API from "../services/api";
import DesignerCanvas from "../components/designer/DesignerCanvas";
import DesignerPropertiesPanel from "../components/designer/properties/DesignerPropertiesPanel";
import DesignerLayersPanel from "../components/designer/DesignerLayersPanel";
import DesignerAddObjectPanel from "../components/designer/DesignerAddObjectPanel";
import DesignerPageHeaderControls from "../components/designer/page/DesignerPageHeaderControls";
import DesignerOrderPanel from "../components/designer/page/DesignerOrderPanel";
import {
    moveSelectedOneStepGlobal,
    moveSelectedToEdgeGlobal,
    moveSelectedOneStepOverlap,
} from "../utils/designerLayerUtils";
import {createDefaultObjectByType} from "../utils/designerObjectFactory";
import {createDuplicatedObject} from "../utils/designerDuplicateUtils";
import {
    getObjectsByGroupId,
    normalizeGroupZIndexes,
    duplicateGroupObjects,
    getGroupRepresentative,
} from "../utils/designerGroupUtils";
import {
    prepareObjectCopyForPage,
    preparePageCopyPayload,
} from "../utils/designerCopyUtils";

const DesignerPage = () => {
    const [pages, setPages] = useState([]);
    const [selectedPageId, setSelectedPageId] = useState("");
    const [selectedPage, setSelectedPage] = useState(null);
    const [objects, setObjects] = useState([]);
    const [originalObjects, setOriginalObjects] = useState([]);
    const [deletedObjectIds, setDeletedObjectIds] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedStandaloneObjectIds, setSelectedStandaloneObjectIds] = useState([]);
    const [latestTags, setLatestTags] = useState([]);
    const [newPageTitle, setNewPageTitle] = useState("");
    const [creatingPage, setCreatingPage] = useState(false);
    const [reorderingPages, setReorderingPages] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);

    const handleUpdatePageSettings = async (patch) => {
        if (!selectedPageId || !selectedPage) return;

        setSelectedPage((prev) => ({
            ...prev,
            ...patch,
        }));

        setPages((prev) =>
            prev.map((page) =>
                String(page.id) === String(selectedPageId)
                    ? {...page, ...patch}
                    : page
            )
        );

        try {
            const res = await API.patch(`/designer/pages/${selectedPageId}/`, patch);

            setSelectedPage(res.data);

            setPages((prev) =>
                prev.map((page) =>
                    String(page.id) === String(selectedPageId) ? res.data : page
                )
            );
            setMessage("Page settings updated.");
            window.dispatchEvent(new Event("aqueris-pages-changed"));
        } catch (err) {
            console.error("Failed to update page settings:", err);
            setError("Failed to update page settings.");
        }
    };
    const handleDuplicatePage = async () => {
        if (!selectedPageId || !selectedPage) return;

        try {
            setError("");
            setMessage("");

            const pagePayload = preparePageCopyPayload(selectedPage);
            const pageRes = await API.post("/designer/pages/", pagePayload);
            const newPage = pageRes.data;

            const objectsRes = await API.get(`/designer/pages/${selectedPageId}/objects/`);
            const sourceObjects = objectsRes.data?.objects || [];

            await Promise.all(
                sourceObjects.map((obj) =>
                    API.post(
                        "/designer/objects/",
                        prepareObjectCopyForPage(obj, newPage.id, {
                            keepPosition: true,
                            group: null,
                            zIndex: obj.z_index,
                        })
                    )
                )
            );

            await fetchPages();
            setSelectedPageId(String(newPage.id));
            setMessage(`Page "${selectedPage.title}" duplicated.`);
            window.dispatchEvent(new Event("aqueris-pages-changed"));
        } catch (err) {
            console.error("Failed to duplicate page:", err);
            setError("Failed to duplicate page.");
        }
    };
    const handleDeletePage = async () => {
        if (!selectedPageId || !selectedPage) return;
        if (!window.confirm(`Delete page “${selectedPage.title}” and all of its designer objects?`)) return;

        try {
            setError("");
            setMessage("");
            await API.delete(`/designer/pages/${selectedPageId}/`);
            const remainingPages = pages.filter((page) => String(page.id) !== String(selectedPageId));
            setPages(remainingPages);
            setSelectedPage(null);
            setObjects([]);
            setOriginalObjects([]);
            setDeletedObjectIds([]);
            clearSelection();
            setSelectedPageId(remainingPages.length ? String(remainingPages[0].id) : "");
            setMessage(`Page "${selectedPage.title}" deleted.`);
            window.dispatchEvent(new Event("aqueris-pages-changed"));
        } catch (err) {
            console.error("Failed to delete page:", err);
            setError("Failed to delete page.");
        }
    };
    const objectsById = useMemo(() => {
        const map = new Map();
        objects.forEach((obj) => map.set(obj.id, obj));
        return map;
    }, [objects]);

    const groupedObjectsMap = useMemo(() => {
        const map = new Map();
        objects.forEach((obj) => {
            if (!obj.group) return;
            if (!map.has(obj.group)) map.set(obj.group, []);
            map.get(obj.group).push(obj);
        });
        return map;
    }, [objects]);

    const selectedGroupObjects = useMemo(() => {
        if (!selectedGroupId) return [];
        return groupedObjectsMap.get(selectedGroupId) || [];
    }, [groupedObjectsMap, selectedGroupId]);

    const selectedObjects = useMemo(() => {
        if (selectedGroupId) return selectedGroupObjects;

        return selectedStandaloneObjectIds
            .map((id) => objectsById.get(id))
            .filter(Boolean);
    }, [selectedGroupId, selectedGroupObjects, selectedStandaloneObjectIds, objectsById]);

    const selectedObjectIds = useMemo(
        () => selectedObjects.map((obj) => obj.id),
        [selectedObjects]
    );

    const selectedObject = useMemo(() => {
        if (selectedGroupId) {
            return getGroupRepresentative(objects, selectedGroupId);
        }
        return selectedObjects[0] || null;
    }, [selectedGroupId, selectedObjects, objects]);

    const isMultiSelected = selectedObjects.length > 1;
    const hasGroupSelection = Boolean(selectedGroupId);

    const changedObjects = useMemo(() => {
        const originalMap = new Map(originalObjects.map((obj) => [obj.id, obj]));

        return objects.filter((obj) => {
            if (obj.isNew) return true;
            const original = originalMap.get(obj.id);
            if (!original) return false;
            return JSON.stringify(obj) !== JSON.stringify(original);
        });
    }, [objects, originalObjects]);

    const hasUnsavedChanges =
        changedObjects.length > 0 || deletedObjectIds.length > 0;

    const fetchPages = async () => {
        try {
            const res = await API.get("/designer/pages/");
            const pageList = res.data || [];
            setPages(pageList);

            if (pageList.length > 0 && !selectedPageId) {
                setSelectedPageId(String(pageList[0].id));
            }
        } catch (err) {
            console.error("Failed to load designer pages:", err);
            setError("Failed to load designer pages.");
        }
    };

    const handleReorderPages = async (orderedPages) => {
        if (reorderingPages) return;
        const previousPages = pages;
        setPages(orderedPages);
        setReorderingPages(true);
        setError("");
        setMessage("");

        try {
            const response = await API.post("/designer/pages/reorder/", {
                page_ids: orderedPages.map((page) => Number(page.id)),
            });
            setPages(Array.isArray(response.data) ? response.data : orderedPages);
            setMessage("Page order updated.");
            window.dispatchEvent(new Event("aqueris-pages-changed"));
        } catch (err) {
            console.error("Failed to reorder designer pages:", err);
            setPages(previousPages);
            setError("Failed to update page order. The previous order was restored.");
            await fetchPages();
        } finally {
            setReorderingPages(false);
        }
    };

    const fetchPageDetail = async (pageId) => {
        try {
            const res = await API.get(`/designer/pages/${pageId}/`);
            setSelectedPage(res.data);
        } catch (err) {
            console.error("Failed to load page detail:", err);
            setError("Failed to load page detail.");
        }
    };

    const fetchPageObjects = async (pageId) => {
        try {
            const res = await API.get(`/designer/pages/${pageId}/objects/`);
            const loadedObjects = res.data?.objects || [];
            setObjects(loadedObjects);
            setOriginalObjects(loadedObjects);
            setDeletedObjectIds([]);
        } catch (err) {
            console.error("Failed to load page objects:", err);
            setError("Failed to load page objects.");
        }
    };

    const fetchLatestTags = async () => {
        try {
            const res = await API.get("/latest-tag-values/");
            setLatestTags(res.data || []);
        } catch (err) {
            console.error("Failed to load latest tags:", err);
        }
    };

    const handleSelectObject = (objectId, multi = false) => {
        const target = objectsById.get(objectId);
        if (!target) return;

        if (target.group) {
            setSelectedGroupId(target.group);
            setSelectedStandaloneObjectIds([]);
            return;
        }

        setSelectedGroupId(null);

        setSelectedStandaloneObjectIds((prev) => {
            if (!multi) return [objectId];
            if (prev.includes(objectId)) {
                return prev.filter((id) => id !== objectId);
            }
            return [...prev, objectId];
        });
    };

    const clearSelection = () => {
        setSelectedGroupId(null);
        setSelectedStandaloneObjectIds([]);
    };

    const handleCreatePage = async () => {
        const title = newPageTitle.trim();

        if (!title) {
            setError("Page title is required.");
            return;
        }

        try {
            setCreatingPage(true);
            setError("");
            setMessage("");

            const payload = {
                title,
                description: "",
                width: 1600,
                height: 900,
                background_color: "#ffffff",
                background_image: "",
                show_grid: true,
                grid_size: 20,
                is_active: true,
            };

            const res = await API.post("/designer/pages/", payload);
            const createdPage = res.data;

            setPages((prev) => [...prev, createdPage]);
            setNewPageTitle("");
            setSelectedPageId(String(createdPage.id));
            setMessage(`Page "${createdPage.title}" created successfully.`);
        } catch (err) {
            console.error("Failed to create page:", err);
            setError("Failed to create page.");
        } finally {
            setCreatingPage(false);
        }
    };

    const executeButtonAction = async (targetObject) => {
        const data = targetObject?.data_json || {};

        if (
            targetObject?.type === "tag" &&
            (data.object_mode || "monitor") === "control"
        ) {
            const tagName = data.tag_name;
            const writeValue = targetObject.__controlValue;
            const controlType =
                targetObject.__controlType || data.control_type || "digital";

            if (!tagName) {
                alert("No real tag selected for this tag control.");
                return;
            }

            if (
                writeValue === "" ||
                writeValue === null ||
                writeValue === undefined
            ) {
                alert("Please enter a value.");
                return;
            }

            try {
                await API.post("/write-tag/", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: controlType,
                });
                await fetchLatestTags();
            } catch (err) {
                console.error("Failed to write tag from tag control:", err);
                alert("Failed to write real tag.");
            }
            return;
        }

        const actionType = data.action_type || "none";

        if (actionType === "tag_write") {
            const tagName = data.tag_name;
            const writeValue = data.write_value;

            if (!tagName) {
                alert("No real tag selected for this button.");
                return;
            }

            try {
                await API.post("/write-tag/", {
                    tag_name: tagName,
                    value: writeValue,
                    control_type: "digital",
                });
                await fetchLatestTags();
            } catch (err) {
                console.error("Failed to write tag from button:", err);
                alert("Failed to write real tag.");
            }
            return;
        }

        if (actionType === "link") {
            const url = data.url;
            const openInNewTab = data.open_in_new_tab ?? true;

            if (!url) {
                alert("No URL configured for this button.");
                return;
            }

            if (openInNewTab) {
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                window.location.href = url;
            }
        }
    };

    const updateObjectPosition = (objectId, newX, newY) => {
        setObjects((prev) =>
            prev.map((obj) =>
                String(obj.id) === String(objectId) ? {...obj, x: newX, y: newY} : obj
            )
        );
    };

    const updateObjectRect = (objectId, nextRect) => {
        setObjects((prev) =>
            prev.map((obj) =>
                String(obj.id) === String(objectId)
                    ? {
                        ...obj,
                        x: nextRect.x,
                        y: nextRect.y,
                        width: nextRect.width,
                        height: nextRect.height,
                    }
                    : obj
            )
        );
    };

    const updateObjectsBulk = (nextObjects) => {
        setObjects(nextObjects);
    };

    const updateObjectFields = (objectId, patch) => {
        setObjects((prev) =>
            prev.map((obj) => (obj.id === objectId ? {...obj, ...patch} : obj))
        );
    };

    const updateObjectStyleField = (objectId, key, value) => {
        setObjects((prev) =>
            prev.map((obj) =>
                obj.id === objectId
                    ? {
                        ...obj,
                        style_json: {
                            ...(obj.style_json || {}),
                            [key]: value,
                        },
                    }
                    : obj
            )
        );
    };

    const updateObjectDataField = (objectId, key, value) => {
        setObjects((prev) =>
            prev.map((obj) =>
                obj.id === objectId
                    ? {
                        ...obj,
                        data_json: {
                            ...(obj.data_json || {}),
                            [key]: value,
                        },
                    }
                    : obj
            )
        );
    };

    const handleAddObject = (type) => {
        if (!selectedPageId) return;

        const maxZ = objects.length
            ? Math.max(...objects.map((obj) => obj.z_index ?? 0))
            : -1;

        const newObject = {
            ...createDefaultObjectByType(type, Number(selectedPageId), maxZ + 1),
            id: `temp-${Date.now()}`,
            isNew: true,
        };

        setObjects((prev) => [...prev, newObject]);
        setSelectedGroupId(null);
        setSelectedStandaloneObjectIds([newObject.id]);
        setMessage(`${type} object added.`);
        setError("");
    };

    const handleDeleteObject = (objectId) => {
        const target = objects.find((obj) => obj.id === objectId);
        if (!target) return;

        if (target.group) {
            const groupObjects = getObjectsByGroupId(objects, target.group);
            const confirmed = window.confirm(
                `Delete group with ${groupObjects.length} objects?`
            );
            if (!confirmed) return;

            const existingIdsToDelete = groupObjects
                .filter((obj) => !String(obj.id).startsWith("temp-"))
                .map((obj) => obj.id);

            setDeletedObjectIds((prev) => [...prev, ...existingIdsToDelete]);
            setObjects((prev) => prev.filter((obj) => obj.group !== target.group));
            clearSelection();
            setMessage("Group removed from canvas.");
            setError("");
            return;
        }

        const confirmed = window.confirm(`Delete "${target.name}"?`);
        if (!confirmed) return;

        if (!String(target.id).startsWith("temp-")) {
            setDeletedObjectIds((prev) => [...prev, target.id]);
        }

        setObjects((prev) => prev.filter((obj) => obj.id !== objectId));
        setSelectedStandaloneObjectIds((prev) => prev.filter((id) => id !== objectId));
        setMessage("Object removed from canvas.");
        setError("");
    };

    const handleDeleteSelected = () => {
        if (selectedObjectIds.length === 0) return;

        if (selectedGroupId) {
            const groupObjects = getObjectsByGroupId(objects, selectedGroupId);
            const confirmed = window.confirm(
                `Delete group with ${groupObjects.length} objects?`
            );
            if (!confirmed) return;

            const existingIdsToDelete = groupObjects
                .filter((obj) => !String(obj.id).startsWith("temp-"))
                .map((obj) => obj.id);

            setDeletedObjectIds((prev) => [...prev, ...existingIdsToDelete]);
            setObjects((prev) => prev.filter((obj) => obj.group !== selectedGroupId));
            clearSelection();
            setMessage("Selected group removed from canvas.");
            setError("");
            return;
        }

        const targets = objects.filter((obj) => selectedObjectIds.includes(obj.id));
        if (!targets.length) return;

        const confirmed = window.confirm(
            targets.length === 1
                ? `Delete "${targets[0].name}"?`
                : `Delete ${targets.length} selected objects?`
        );
        if (!confirmed) return;

        const existingIdsToDelete = targets
            .filter((obj) => !String(obj.id).startsWith("temp-"))
            .map((obj) => obj.id);

        setDeletedObjectIds((prev) => [...prev, ...existingIdsToDelete]);
        setObjects((prev) => prev.filter((obj) => !selectedObjectIds.includes(obj.id)));
        clearSelection();
        setMessage("Selected objects removed from canvas.");
        setError("");
    };

    const handleDuplicateObject = async (objectId) => {
        const sourceObject = objects.find((obj) => obj.id === objectId);
        if (!sourceObject) return;

        if (sourceObject.group) {
            try {
                setError("");
                setMessage("");

                const groupResponse = await API.post("/designer/groups/", {
                    page: Number(selectedPageId),
                    title: `Group Copy ${Date.now()}`,
                    description: "",
                });

                const newGroupId = groupResponse.data?.id;
                if (!newGroupId) {
                    setError("Failed to create duplicate group.");
                    return;
                }

                const duplicated = duplicateGroupObjects(objects, sourceObject.group, newGroupId, {
                    offsetX: 20,
                    offsetY: 20,
                    nameSuffix: "copy",
                });

                setObjects((prev) => [...prev, ...duplicated]);
                setSelectedGroupId(newGroupId);
                setSelectedStandaloneObjectIds([]);
                setMessage("Group duplicated.");
            } catch (err) {
                console.error("Failed to duplicate group:", err);
                setError("Failed to duplicate group.");
            }
            return;
        }

        const duplicatedObject = createDuplicatedObject(sourceObject, objects, {
            offsetX: 20,
            offsetY: 20,
            nameSuffix: "copy",
        });

        if (!duplicatedObject) return;

        setObjects((prev) => [...prev, duplicatedObject]);
        setSelectedGroupId(null);
        setSelectedStandaloneObjectIds([duplicatedObject.id]);
        setMessage(`Object "${sourceObject.name}" duplicated.`);
        setError("");
    };

    const handleBringForwardObject = (objectId) => {
        const target = objectsById.get(objectId);
        if (!target) return;

        if (target.group) {
            setSelectedGroupId(target.group);
            setSelectedStandaloneObjectIds([]);
        } else {
            setSelectedGroupId(null);
            setSelectedStandaloneObjectIds([objectId]);
        }

        setObjects((prev) => moveSelectedOneStepGlobal(prev, objectId, "forward"));
    };

    const handleSendBackwardObject = (objectId) => {
        const target = objectsById.get(objectId);
        if (!target) return;

        if (target.group) {
            setSelectedGroupId(target.group);
            setSelectedStandaloneObjectIds([]);
        } else {
            setSelectedGroupId(null);
            setSelectedStandaloneObjectIds([objectId]);
        }

        setObjects((prev) => moveSelectedOneStepGlobal(prev, objectId, "backward"));
    };

    const handleBringToFrontObject = (objectId) => {
        const target = objectsById.get(objectId);
        if (!target) return;

        if (target.group) {
            setSelectedGroupId(target.group);
            setSelectedStandaloneObjectIds([]);
        } else {
            setSelectedGroupId(null);
            setSelectedStandaloneObjectIds([objectId]);
        }

        setObjects((prev) => moveSelectedToEdgeGlobal(prev, objectId, "front"));
    };

    const handleSendToBackObject = (objectId) => {
        const target = objectsById.get(objectId);
        if (!target) return;

        if (target.group) {
            setSelectedGroupId(target.group);
            setSelectedStandaloneObjectIds([]);
        } else {
            setSelectedGroupId(null);
            setSelectedStandaloneObjectIds([objectId]);
        }

        setObjects((prev) => moveSelectedToEdgeGlobal(prev, objectId, "back"));
    };

    const handleBringForward = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedOneStepGlobal(prev, targetId, "forward"));
    };

    const handleSendBackward = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedOneStepGlobal(prev, targetId, "backward"));
    };

    const handleBringToFront = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedToEdgeGlobal(prev, targetId, "front"));
    };

    const handleSendToBack = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedToEdgeGlobal(prev, targetId, "back"));
    };

    const handleBringForwardOverlap = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedOneStepOverlap(prev, targetId, "forward"));
    };

    const handleSendBackwardOverlap = () => {
        if (selectedObjectIds.length !== 1) return;
        const targetId = selectedObjectIds[0];
        setObjects((prev) => moveSelectedOneStepOverlap(prev, targetId, "backward"));
    };

    const handleCreateGroup = async () => {
        if (!selectedPageId || selectedObjectIds.length < 2) {
            setError("Select at least two objects to create a group.");
            return;
        }

        try {
            setError("");
            setMessage("");

            const response = await API.post("/designer/groups/", {
                page: Number(selectedPageId),
                title: `Group ${Date.now()}`,
                description: "",
            });

            const groupId = response.data?.id;
            if (!groupId) {
                setError("Group was not created correctly.");
                return;
            }

            await Promise.all(
                selectedObjectIds.map((objectId) =>
                    API.patch(`/designer/objects/${objectId}/`, {
                        group: groupId,
                    })
                )
            );

            const updatedObjects = objects.map((obj) =>
                selectedObjectIds.includes(obj.id) ? {...obj, group: groupId} : obj
            );

            const normalized = normalizeGroupZIndexes(updatedObjects, groupId);
            setObjects(normalized);

            await Promise.all(
                normalized
                    .filter((obj) => selectedObjectIds.includes(obj.id))
                    .map((obj) =>
                        API.patch(`/designer/objects/${obj.id}/`, {
                            z_index: obj.z_index,
                        })
                    )
            );

            setSelectedGroupId(groupId);
            setSelectedStandaloneObjectIds([]);
            setMessage("Group created successfully.");
        } catch (err) {
            console.error("Failed to create group:", err);
            setError("Failed to create group.");
        }
    };

    const handleUngroupSelected = async () => {
        if (selectedObjectIds.length === 0) {
            setError("Select objects to ungroup.");
            return;
        }

        try {
            setError("");
            setMessage("");

            await Promise.all(
                selectedObjectIds.map((objectId) =>
                    API.patch(`/designer/objects/${objectId}/`, {
                        group: null,
                    })
                )
            );

            setObjects((prev) =>
                prev.map((obj) =>
                    selectedObjectIds.includes(obj.id) ? {...obj, group: null} : obj
                )
            );

            clearSelection();
            setMessage("Objects ungrouped successfully.");
        } catch (err) {
            console.error("Failed to ungroup objects:", err);
            setError("Failed to ungroup objects.");
        }
    };

    const handleSaveChanges = async () => {
        if (!hasUnsavedChanges) {
            setMessage("No changes to save.");
            return;
        }

        try {
            setSaving(true);
            setError("");
            setMessage("");

            const newObjects = objects.filter((obj) => obj.isNew);
            const existingChangedObjects = changedObjects.filter((obj) => !obj.isNew);

            await Promise.all(
                newObjects.map((obj) =>
                    API.post("/designer/objects/", {
                        page: obj.page,
                        group: obj.group ?? null,
                        type: obj.type,
                        name: obj.name,
                        x: obj.x,
                        y: obj.y,
                        width: obj.width,
                        height: obj.height,
                        rotation: obj.rotation,
                        z_index: obj.z_index,
                        opacity: obj.opacity,
                        visible: obj.visible,
                        locked: obj.locked,
                        style_json: obj.style_json,
                        data_json: obj.data_json,
                        meta_json: obj.meta_json,
                    })
                )
            );

            await Promise.all(
                existingChangedObjects.map((obj) =>
                    API.patch(`/designer/objects/${obj.id}/`, {
                        group: obj.group ?? null,
                        x: obj.x,
                        y: obj.y,
                        width: obj.width,
                        height: obj.height,
                        rotation: obj.rotation,
                        z_index: obj.z_index,
                        opacity: obj.opacity,
                        visible: obj.visible,
                        locked: obj.locked,
                        style_json: obj.style_json,
                        data_json: obj.data_json,
                        meta_json: obj.meta_json,
                    })
                )
            );

            await Promise.all(
                deletedObjectIds.map((id) => API.delete(`/designer/objects/${id}/`))
            );

            await fetchPageObjects(selectedPageId);
            setMessage("Changes saved successfully.");
        } catch (err) {
            console.error("Failed to save changes:", err);
            setError("Failed to save changes.");
        } finally {
            setSaving(false);
        }
    };

    const handleResetChanges = () => {
        setObjects(originalObjects);
        setDeletedObjectIds([]);
        clearSelection();
        setMessage("Unsaved changes were discarded.");
        setError("");
    };

    useEffect(() => {
        fetchPages();
        fetchLatestTags();

        const interval = setInterval(fetchLatestTags, 3000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!selectedPageId) return;

        setLoading(true);
        setError("");
        setMessage("");
        clearSelection();

        Promise.all([
            fetchPageDetail(selectedPageId),
            fetchPageObjects(selectedPageId),
        ]).finally(() => setLoading(false));
    }, [selectedPageId]);

    const rightColumnWidth = propertiesCollapsed ? "76px" : "340px";

    return (
        <div
            style={{
                padding: 20,
                height: "100vh",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <h1 style={{marginTop: 0, marginBottom: 12, flex: "0 0 auto"}}>
                Designer Viewer
            </h1>

            {loading && <p style={{flex: "0 0 auto"}}>Loading...</p>}
            {error && <p style={{color: "red", flex: "0 0 auto"}}>{error}</p>}
            {message && <p style={{color: "green", flex: "0 0 auto"}}>{message}</p>}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: `280px minmax(0, 1fr) ${rightColumnWidth}`,
                    gap: 16,
                    alignItems: "stretch",
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        minHeight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        paddingRight: 4,
                    }}
                >
                    <DesignerPageHeaderControls
                        pages={pages}
                        selectedPageId={selectedPageId}
                        setSelectedPageId={setSelectedPageId}
                        selectedPage={selectedPage}
                        newPageTitle={newPageTitle}
                        setNewPageTitle={setNewPageTitle}
                        creatingPage={creatingPage}
                        onCreatePage={handleCreatePage}
                        saving={saving}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onSaveChanges={handleSaveChanges}
                        onResetChanges={handleResetChanges}
                        onUpdatePageSettings={handleUpdatePageSettings}
                        onDuplicatePage={handleDuplicatePage}
                        onDeletePage={handleDeletePage}
                        onReorderPages={handleReorderPages}
                        reorderingPages={reorderingPages}
                    />

                    <DesignerOrderPanel
                        selectedObject={selectedObject}
                        selectedObjects={selectedObjects}
                        selectedObjectIds={selectedObjectIds}
                        isMultiSelected={isMultiSelected}
                        hasGroupSelection={hasGroupSelection}
                        selectedGroupId={selectedGroupId}
                        onBringForward={handleBringForward}
                        onSendBackward={handleSendBackward}
                        onBringToFront={handleBringToFront}
                        onSendToBack={handleSendToBack}
                        onBringForwardOverlap={handleBringForwardOverlap}
                        onSendBackwardOverlap={handleSendBackwardOverlap}
                        onDeleteSelected={handleDeleteSelected}
                        onCreateGroup={handleCreateGroup}
                        onUngroupSelected={handleUngroupSelected}
                    />

                    <DesignerAddObjectPanel onAddObject={handleAddObject}/>

                    <div style={{minHeight: 260, flex: "0 0 auto"}}>
                        <DesignerLayersPanel
                            objects={objects}
                            selectedObjectIds={selectedObjectIds}
                            selectedGroupId={selectedGroupId}
                            setSelectedObjectIds={setSelectedStandaloneObjectIds}
                            onSelectObject={handleSelectObject}
                            updateObjectFields={updateObjectFields}
                            onDeleteObject={handleDeleteObject}
                            onDuplicateObject={handleDuplicateObject}
                        />
                    </div>
                </div>

                <div
                    style={{
                        minWidth: 0,
                        minHeight: 0,
                        overflow: "hidden",
                        display: "flex",
                    }}
                >
                    {selectedPage && (
                        <DesignerCanvas
                            page={selectedPage}
                            objects={objects}
                            latestTags={latestTags}
                            selectedObjectIds={selectedObjectIds}
                            selectedGroupId={selectedGroupId}
                            setSelectedObjectIds={setSelectedStandaloneObjectIds}
                            onSelectObject={handleSelectObject}
                            clearSelection={clearSelection}
                            updateObjectPosition={updateObjectPosition}
                            updateObjectRect={updateObjectRect}
                            updateObjectsBulk={updateObjectsBulk}
                            updateObjectDataField={updateObjectDataField}
                            onButtonAction={executeButtonAction}
                            onDuplicateObject={handleDuplicateObject}
                            onDeleteObject={handleDeleteObject}
                            onBringForwardObject={handleBringForwardObject}
                            onSendBackwardObject={handleSendBackwardObject}
                            onBringToFrontObject={handleBringToFrontObject}
                            onSendToBackObject={handleSendToBackObject}
                        />
                    )}
                </div>

                <div
                    style={{
                        minHeight: 0,
                        minWidth: 0,
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: propertiesCollapsed ? "flex-end" : "stretch",
                    }}
                >
                    <DesignerPropertiesPanel
                        selectedObject={selectedObject}
                        latestTags={latestTags}
                        updateObjectFields={updateObjectFields}
                        updateObjectStyleField={updateObjectStyleField}
                        updateObjectDataField={updateObjectDataField}
                        collapsed={propertiesCollapsed}
                        setCollapsed={setPropertiesCollapsed}
                    />
                </div>
            </div>
        </div>
    );
};

export default DesignerPage;
